import { useCallback, useMemo } from 'react';
import Analytics from './Analytics';
import { SEGMENT_PRODUCT_NAME } from '../constants/constants';

type IQuestion =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'buildingId'
  | 'coverageStart'
  | 'address.addressNumber'
  | 'address.street'
  | 'address.municipality'
  | 'address.state'
  | 'address.postalCode'
  | 'buildingUnit'
  | 'additionalInterests.x.firstName'
  | 'additionalInterests.x.lastName'
  | 'additionalInterests.x.email'
  | 'additionalInsuredRoommates.x.firstName'
  | 'additionalInsuredRoommates.x.lastName'
  | 'additionalInsuredRoommates.x.email'
  | 'personalPropertyCoverage'
  | 'personalLiabilityCoverage'
  | 'medicalPayments'
  | 'lossOfUse'
  | 'deductible'
  | 'theft'
  | 'addressName'
  | 'waterBackup'
  | 'petDamage';

const questionTitles: { [k in IQuestion]: string } = {
  firstName: 'First name',
  lastName: 'Last name',
  email: 'Email',
  phone: 'Phone Number',
  buildingId: 'Building ID',
  'address.addressNumber': 'Address number',
  'address.street': 'Street',
  'address.municipality': 'Municipality',
  'address.state': 'State',
  'address.postalCode': 'Postal code',
  buildingUnit: 'Building unit',
  coverageStart: 'Coverage start',
  'additionalInterests.x.firstName': 'Additional interest first name',
  'additionalInterests.x.lastName': 'Additional interest last name',
  'additionalInterests.x.email': 'Additional interest email',
  'additionalInsuredRoommates.x.firstName': 'Additional insured first name',
  'additionalInsuredRoommates.x.lastName': 'Additional insured last name',
  'additionalInsuredRoommates.x.email': 'Additional insured email',
  personalPropertyCoverage: 'Personal property coverage',
  personalLiabilityCoverage: 'Personal liability coverage',
  medicalPayments: 'Medical payments',
  lossOfUse: 'Loss of use',
  deductible: 'Deductible',
  theft: 'Theft',
  waterBackup: 'Water backup',
  petDamage: 'Pet damage',
  addressName: 'Street Address',
};

const questionSequenceNos: { [k in IQuestion]: number } = {
  firstName: 1,
  lastName: 2,
  email: 3,
  phone: 4,
  buildingId: 5,
  coverageStart: 26,
  addressName: 27,
  'address.addressNumber': 6,
  'address.street': 7,
  'address.municipality': 8,
  'address.state': 9,
  'address.postalCode': 10,
  buildingUnit: 11,
  'additionalInterests.x.firstName': 12,
  'additionalInterests.x.lastName': 13,
  'additionalInterests.x.email': 14,
  'additionalInsuredRoommates.x.firstName': 15,
  'additionalInsuredRoommates.x.lastName': 16,
  'additionalInsuredRoommates.x.email': 17,
  personalPropertyCoverage: 18,
  personalLiabilityCoverage: 19,
  medicalPayments: 20,
  lossOfUse: 21,
  deductible: 22,
  theft: 23,
  waterBackup: 24,
  petDamage: 25,
};

export const useTrackEventForForm = (applicationId: string) => {
  const trackEvent = useCallback(
    (_fieldName: string) => {
      let fieldName = _fieldName as IQuestion;
      let index;

      if (
        fieldName.includes('additionalInterests') ||
        fieldName.includes('additionalInsuredRoommates')
      ) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const [group, _index, field] = fieldName.split('.');
        if (!field) return;
        index = _index;
        fieldName = `${group}.x.${field}` as IQuestion;
      }

      if (!questionTitles[fieldName]) return;
      const payload = {
        application_id: applicationId,
        product: SEGMENT_PRODUCT_NAME,
        question_title: questionTitles[fieldName] ?? 'Unknown question',
        question_sequence_no:
          String(questionSequenceNos[fieldName] ?? 0) + String(index ?? ''),
      };
      Analytics.trackEvent(`Question answered`, payload);
    },
    [applicationId],
  );

  return useMemo(
    () => ({
      trackEvent,
    }),
    [trackEvent],
  );
};
